/**
 * The only permissions the backend honours for a support user in a company other than their own.
 * Mirrors `SUPPORT_IMPLIED_PERMISSIONS` in
 * `src/main/java/com/devskiller/auth/security/SecurityUserImpl.java`; keep the two lists in sync.
 */
export const SUPPORT_IMPLIED_PERMISSIONS: readonly string[] = [
  "auth:securitylogs:read",
  "auth:userimports:read",
  "auth:structureimports:read",
  "auth:jobtitles:read",
  "auth:user:read",
  "auth:role:read",
  "auth:team:read",
  "auth:accessrequest:request",
  "auth:accessrequest:read",
  "auth:user:runas",
  "auth:user:activity",
  "auth:user:changes",
  "auth:ai:chat",
];

/**
 * Permissions of the account rather than of the company being viewed. The AI assistant resolves
 * the company per request and checks each tool call against it, so it stays available.
 */
const ACCOUNT_PERMISSIONS: readonly string[] = ["auth:ai:chat", "auth:ai:use"];

const SUPERADMIN = "auth:superadmin";

/** A support/superadmin session is looking at a company other than its own. */
export const isForeignCompany = (currentId?: string, ownId?: string) =>
  !!currentId && !!ownId && currentId !== ownId;

/** `product:role` technical authorities such as `auth:support` or `auth:user` belong to the account. */
const isTechnicalRole = (permission: string) => permission.split(":").length === 2;

/** `auth:team:read:{teamId}` → `auth:team:read`. */
const withoutScope = (permission: string) => permission.split(":").slice(0, 3).join(":");

/**
 * Whether `permission` is usable in the company being viewed. In the own company, and for a
 * superadmin anywhere, that is any authority held. In a foreign company everyone else is limited
 * to support's implied read set: a write permission granted by a home-company role is refused by
 * the backend there, so the UI must not offer it either.
 */
export const hasPermissionInCompany = (
  authorities: readonly string[],
  permission: string,
  foreign: boolean,
) => {
  if (!authorities.includes(permission)) {
    return false;
  }
  if (!foreign || authorities.includes(SUPERADMIN)) {
    return true;
  }
  return (
    isTechnicalRole(permission) ||
    ACCOUNT_PERMISSIONS.includes(permission) ||
    SUPPORT_IMPLIED_PERMISSIONS.includes(withoutScope(permission))
  );
};
